"use client"
import type { FragmentOf } from "@opensea/graphql"
import { readFragment } from "@opensea/graphql"
import {
  BottomSheetBody,
  BottomSheetFooter,
  BottomSheetHeader,
  BottomSheetHeaderTitle,
  FlexCenter,
  Separator,
  SpaceBetween,
  TextBody,
} from "@opensea/ui-kit"
import { useRef } from "react"
import { useFormContext, useWatch } from "react-hook-form"
import { useTranslations } from "use-intl"
import { BackButton } from "../../../../../components/BackButton/BackButton"
import { CreateOrdersSummary } from "../../../../../components/CreateOrdersSummary"
import { convertItemsAndPricingToListings } from "../../helpers"
import type { Listing, ListingFlowFormValues } from "../../types"
import { ListingFlowItemsTable } from "../ListingFlowItemsTable/ListingFlowItemsTable"
import { ListingFlowTimeline } from "../ListingFlowTimeline"
import { ListingFlowReview_itemFragment } from "./ListingFlowReview.fragment"

type ListingFlowReviewProps = {
  onBack: () => void
  onSuccess?: () => void
  onError?: (error: Error) => void
  items: FragmentOf<typeof ListingFlowReview_itemFragment>[]
}

const SEPARATOR = <Separator className="my-1" />

export function ListingFlowReview({
  onBack,
  onSuccess,
  onError,
  items: itemKeys,
}: ListingFlowReviewProps) {
  const t = useTranslations("wallet.ListingFlowReview")
  const { control } = useFormContext<ListingFlowFormValues>()
  const adjustForFees = useWatch({ control, name: "adjustForFees" })
  const applyCreatorFee = useWatch({ control, name: "applyCreatorFee" })
  const formListings = useWatch({ control, name: "listings" })
  const reservedBuyer = useWatch({ control, name: "reservedBuyer" })
  const formValues = useWatch({ control })

  const items = itemKeys.map(item =>
    readFragment(ListingFlowReview_itemFragment, item),
  )

  const startTime = useRef(new Date())
  const listingsRef = useRef<Listing[]>(
    convertItemsAndPricingToListings({
      adjustForFees: adjustForFees ?? false,
      items,
      formValues: formValues as ListingFlowFormValues,
      startTime: startTime.current,
    }),
  )

  const reservedBuyerIdentifier = reservedBuyer?.identifier

  const creatorFeeEnforced = formListings?.some(
    listing => listing.isCreatorFeeRequired,
  )

  return (
    <>
      <BottomSheetHeader>
        <BottomSheetHeaderTitle
          className="cursor-pointer"
          onClick={() => onBack()}
        >
          <FlexCenter className="gap-3">
            <BackButton />
            <Separator className="h-4 text-secondary" orientation="vertical" />
            {t("reviewListing", { count: items.length })}
          </FlexCenter>
        </BottomSheetHeaderTitle>
      </BottomSheetHeader>
      <BottomSheetBody>
        <ListingFlowItemsTable isReview items={items} />

        {reservedBuyerIdentifier && (
          <>
            {SEPARATOR}

            <SpaceBetween className="py-3">
              <TextBody weight="semibold">{t("reservedFor")}</TextBody>
              <TextBody>{reservedBuyerIdentifier}</TextBody>
            </SpaceBetween>
          </>
        )}

        {SEPARATOR}

        {items[0].collection ? (
          <CreateOrdersSummary
            adjustForFees={adjustForFees ?? false}
            collection={items[0].collection}
            creatorFeeEnforced={creatorFeeEnforced ?? false}
            currencyIdentifier={{
              contractAddress:
                items[0].collection.chain.listingCurrency.address,
              chainIdentifier: items[0].collection.chain.identifier,
            }}
            isReview
            orderSide="listing"
            orders={formListings ?? []}
            symbol={items[0].collection.chain.listingCurrency.symbol}
          />
        ) : null}
      </BottomSheetBody>
      <BottomSheetFooter>
        <ListingFlowTimeline
          listings={listingsRef.current}
          onBack={onBack}
          onError={onError}
          onSuccess={onSuccess}
          useCreatorFee={applyCreatorFee ?? false}
        />
      </BottomSheetFooter>
    </>
  )
}
